import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatDate } from "@/lib/dateFormat";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import {
  Plus, Send, Mail, CheckCircle2, Circle, RefreshCw, ShieldCheck,
  Loader2, X, Pencil, Clock, Download, FlaskConical, ChevronDown, ChevronUp,
} from "lucide-react";

function mondayOf(d = new Date()) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // 0=Mon
  date.setDate(date.getDate() - day);
  // Local-date formatting — toISOString() converts to UTC first, which
  // shifts the date back a day for IST wall-clock times 00:00-05:29.
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

const SOURCE_TONE = { milestone: "blue", goal: "green", problem: "orange", outcome: "brand" };

/** Feb 2026 — Weekly Reports revamp. Lives inside each client-linked
 * project's page (not a global nav page). Anchors the report to the
 * project's milestones + SOW goals/problems/outcomes as an editable
 * per-scope grid, auto-prefilled from this week's timesheets. */
export default function ProjectWeeklyReportSection({ project, canEdit }) {
  const { user } = useAuth();
  const can = usePermissions();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [historyShown, setHistoryShown] = useState(10);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/weekly-summary", { params: { project_id: project.id } });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const weekStart = useMemo(() => mondayOf(), []);
  // Legacy test data can have multiple docs sharing the same week_start
  // (pre-dating the one-report-per-week convention) — pick the most
  // recently created one as "current" so stale auto-generated stubs don't
  // shadow a newer, real report.
  const currentCandidates = rows.filter((r) => r.week_start === weekStart);
  const current = currentCandidates.length
    ? currentCandidates.slice().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0]
    : null;
  const history = rows
    .filter((r) => r.week_start !== weekStart)
    .sort((a, b) => (b.week_start || "").localeCompare(a.week_start || ""));

  // Phase 136 — role-in-set converted to the matrix feature that gates
  // every other weekly-report action (send/submit/edit); client_manager
  // keeps its identity-based "owns client comms" exception (backend parity).
  const isMgr = can("weekly_reports") || user?.role === "client_manager";
  const isTeamMember = (project.team_ids || []).includes(user?.id);

  const startWeek = async () => {
    setStarting(true);
    try {
      const { data: tpl } = await api.get("/weekly-summary/scope-template", {
        params: { project_id: project.id, week_start: weekStart },
      });
      await api.post("/weekly-summary", {
        project_id: project.id, week_start: weekStart,
        highlights: "", next_week: "", blockers: "",
        scope_rows: tpl.scope_rows, founders_note: "",
      });
      toast.success("This week's report started");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStarting(false); }
  };

  return (
    <div data-testid="project-weekly-report-section" className="space-y-4">
      <div className="card-flat p-4 bg-gradient-to-r from-slate-50 to-orange-50 border-l-4 border-l-[#F54800]" data-testid="weekly-report-week-banner">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="label-overline text-[#F54800] inline-flex items-center gap-1"><Clock size={11} /> This week</div>
            <p className="text-sm text-slate-700 mt-1">Week of {formatDate(weekStart)} — anchored to milestones + SOW scope, prefilled from timesheets.</p>
          </div>
          {!current && canEdit && (
            <Button onClick={startWeek} disabled={starting} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="start-weekly-report-btn">
              {starting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Plus size={14} className="mr-1.5" />}
              {starting ? "Starting…" : "Start this week's report"}
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 p-4">Loading…</div>
      ) : current ? (
        <WeeklyReportCard row={current} project={project} isMgr={isMgr} isTeamMember={isTeamMember} onReload={load} defaultOpen />
      ) : (
        <div className="card-flat p-6 text-center text-sm text-slate-500" data-testid="no-current-weekly-report">
          No report started for this week yet.
        </div>
      )}

      {history.length > 0 && (
        <div>
          <div className="label-overline mb-2">Past reports ({history.length})</div>
          <div className="space-y-3">
            {history.slice(0, historyShown).map((r) => (
              <WeeklyReportCard key={r.id} row={r} project={project} isMgr={isMgr} isTeamMember={isTeamMember} onReload={load} />
            ))}
          </div>
          {history.length > historyShown && (
            <Button
              type="button" variant="outline" size="sm" className="rounded-sm mt-3"
              onClick={() => setHistoryShown((n) => n + 10)}
              data-testid="show-more-weekly-history"
            >
              Show more ({history.length - historyShown} remaining)
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function statusMeta(row) {
  const status = row.sent_at ? "sent" : (row.status || "draft");
  return {
    draft:     { label: "Draft",                   tone: "slate", key: status },
    submitted: { label: "Submitted for review",     tone: "blue",  key: status },
    approved:  { label: "Approved · ready to send", tone: "green", key: status },
    rejected:  { label: "Sent back · please revise", tone: "red",  key: status },
    sent:      { label: "Sent to client",           tone: "green", key: status },
  }[status] || { label: status, tone: "slate", key: status };
}

function WeeklyReportCard({ row, project, isMgr, isTeamMember, onReload, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [form, setForm] = useState(() => seedForm(row));
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [deliverableInput, setDeliverableInput] = useState("");
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => { setForm(seedForm(row)); }, [row]);

  function seedForm(r) {
    return {
      scope_rows: (r.scope_rows || []).map((sr, i) => ({ ...sr, id: sr.id || `row-${i}` })),
      founders_note: r.founders_note || "",
      blockers: r.blockers || "",
      deliverables: (r.deliverables || []).map((d, i) => ({
        _uid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `d-${i}`,
        label: d.label, done: !!d.done,
      })),
    };
  }

  const meta = statusMeta(row);
  const isLocked = meta.key === "approved" || meta.key === "sent" || !!row.sent_at;
  const canEditContent = isMgr || (isTeamMember && !isLocked);
  const canSubmit = isMgr && (meta.key === "draft" || meta.key === "rejected");
  // Phase 141 — strict gate: Email to Client is disabled until a Founder
  // has marked the report Approved. No more admin/CM force-send escape hatch.
  const canSend = meta.key === "approved" || meta.key === "sent";

  const overallPct = form.scope_rows.length
    ? Math.round(form.scope_rows.reduce((a, r) => a + (Number(r.percent_complete) || 0), 0) / form.scope_rows.length)
    : 0;
  const deliverablesDone = form.deliverables.filter((d) => d.done).length;

  const updateRow = (id, patch) => {
    setForm((f) => ({ ...f, scope_rows: f.scope_rows.map((r) => r.id === id ? { ...r, ...patch } : r) }));
  };

  const addDeliverable = () => {
    const label = deliverableInput.trim();
    if (!label) return;
    const _uid = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `d-${Date.now()}`;
    setForm((f) => ({ ...f, deliverables: [...f.deliverables, { _uid, label, done: false }] }));
    setDeliverableInput("");
  };
  const toggleDeliverable = (uid) => {
    setForm((f) => ({ ...f, deliverables: f.deliverables.map((d) => d._uid === uid ? { ...d, done: !d.done } : d) }));
  };
  const removeDeliverable = (uid) => {
    setForm((f) => ({ ...f, deliverables: f.deliverables.filter((d) => d._uid !== uid) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/weekly-summary/${row.id}`, {
        scope_rows: form.scope_rows.map(({ id, ...rest }) => ({ id, ...rest })),
        founders_note: form.founders_note,
        blockers: form.blockers,
        deliverables: form.deliverables.map(({ _uid: _omit, ...rest }) => rest),
      });
      toast.success("Report saved");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const refreshFromTimesheets = async () => {
    setRefreshing(true);
    try {
      const { data: tpl } = await api.get("/weekly-summary/scope-template", {
        params: { project_id: project.id, week_start: row.week_start },
      });
      const existingByKey = new Map(form.scope_rows.map((r) => [`${r.source}:${r.ref_id || r.label}`, r]));
      const merged = tpl.scope_rows.map((tr) => {
        const key = `${tr.source}:${tr.ref_id || tr.label}`;
        const ex = existingByKey.get(key);
        if (!ex) return { ...tr };
        return { ...ex, progress_this_week: tr.progress_this_week || ex.progress_this_week };
      });
      setForm((f) => ({ ...f, scope_rows: merged }));
      // Persist scope_rows + total_hours together — leaving the page right
      // after Refresh (without a manual Save) must not lose the merge.
      await api.patch(`/weekly-summary/${row.id}`, {
        total_hours: tpl.total_hours,
        scope_rows: merged.map(({ id, ...rest }) => ({ id, ...rest })),
      });
      toast.success(`Refreshed · ${tpl.total_hours}h logged this week`);
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRefreshing(false); }
  };

  const submitForApproval = async () => {
    try {
      await api.post(`/weekly-summary/${row.id}/submit-for-approval`);
      toast.success("Submitted for approval");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const notifySendResult = (data) => {
    if (data?.delivered) { toast.success("Sent to client"); return; }
    if (data?.reason === "missing_api_key") { toast.warning("Saved — set RESEND_API_KEY to actually deliver"); return; }
    if ((data?.error || "").toLowerCase().includes("quota")) {
      toast.warning("Report saved, but the email quota for this inbox is used up for today — try again later.");
      return;
    }
    toast.error("Send failed: " + (data?.error || "unknown"));
  };

  const send = async () => {
    try {
      const { data } = await api.post(`/weekly-summary/${row.id}/send`);
      notifySendResult(data);
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post(`/weekly-summary/${row.id}/send-test`);
      if (data?.delivered) toast.success("Test copy sent to your inbox");
      else if (data?.reason === "missing_api_key") toast.warning("Preview ready — set RESEND_API_KEY to actually deliver");
      else if ((data?.error || "").toLowerCase().includes("quota")) toast.warning("Preview ready, but this inbox's email quota is used up for today — try again later.");
      else toast.error("Send failed: " + (data?.error || "unknown"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingTest(false); }
  };

  return (
    <div className="card-flat" data-testid={`weekly-report-${row.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
        data-testid={`weekly-report-toggle-${row.id}`}
      >
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <Tag tone="brand">Week of {formatDate(row.week_start)}</Tag>
          <Tag tone={meta.tone} data-testid={`weekly-report-status-${row.id}`}>{meta.label}</Tag>
          <Tag tone={overallPct >= 80 ? "green" : overallPct >= 40 ? "orange" : "slate"}>{overallPct}% complete</Tag>
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400 shrink-0" /> : <ChevronDown size={16} className="text-slate-400 shrink-0" />}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-4 space-y-5">
          {/* Stat boxes — matches the Leaves / Attendance dashboard box style */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="card-flat p-3" data-testid={`weekly-stat-hours-${row.id}`}>
              <div className="label-overline inline-flex items-center gap-1"><Clock size={11} /> Total team effort</div>
              <div className="text-2xl font-display font-bold text-slate-900 mt-1">{row.total_hours ?? 0}h</div>
            </div>
            <div className="card-flat p-3" data-testid={`weekly-stat-pct-${row.id}`}>
              <div className="label-overline">Overall scope progress</div>
              <div className="text-2xl font-display font-bold text-slate-900 mt-1">{overallPct}%</div>
            </div>
            <div className="card-flat p-3" data-testid={`weekly-stat-deliverables-${row.id}`}>
              <div className="label-overline">Deliverables</div>
              <div className="text-2xl font-display font-bold text-slate-900 mt-1">{deliverablesDone}/{form.deliverables.length}</div>
            </div>
          </div>

          {/* Scope grid */}
          <div>
            <div className="label-overline mb-2">Scope · milestones + SOW goals/problems/outcomes</div>
            {form.scope_rows.length === 0 ? (
              <div className="text-sm text-slate-500 italic p-3 card-flat">No scope rows yet — click &quot;Refresh from timesheets&quot; to populate from milestones &amp; SOW.</div>
            ) : (
              <div className="card-flat divide-row">
                {form.scope_rows.map((r) => (
                  <div key={r.id} className="p-3 grid grid-cols-1 md:grid-cols-12 gap-2" data-testid={`scope-row-${r.id}`}>
                    <div className="md:col-span-3 min-w-0">
                      <Tag tone={SOURCE_TONE[r.source] || "slate"}>{r.source}</Tag>
                      <div className="text-sm font-medium text-slate-900 mt-1 break-words">{r.label}</div>
                    </div>
                    <div className="md:col-span-4">
                      <Label className="label-overline text-[9px]">Progress this week</Label>
                      <Textarea
                        rows={2} className="rounded-sm mt-1 text-sm"
                        value={r.progress_this_week}
                        onChange={(e) => updateRow(r.id, { progress_this_week: e.target.value })}
                        disabled={!canEditContent}
                        data-testid={`scope-progress-${r.id}`}
                      />
                    </div>
                    <div className="md:col-span-4">
                      <Label className="label-overline text-[9px]">Goals for next week</Label>
                      <Textarea
                        rows={2} className="rounded-sm mt-1 text-sm"
                        value={r.goals_next_week}
                        onChange={(e) => updateRow(r.id, { goals_next_week: e.target.value })}
                        disabled={!canEditContent}
                        data-testid={`scope-next-${r.id}`}
                      />
                    </div>
                    <div className="md:col-span-1">
                      <Label className="label-overline text-[9px]">% done</Label>
                      <Input
                        type="number" min={0} max={100} className="rounded-sm mt-1 text-sm"
                        value={r.percent_complete}
                        onChange={(e) => updateRow(r.id, { percent_complete: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                        disabled={!canEditContent}
                        data-testid={`scope-pct-${r.id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {canEditContent && (
              <Button
                type="button" variant="outline" size="sm" className="rounded-sm mt-2"
                onClick={refreshFromTimesheets} disabled={refreshing}
                data-testid={`refresh-weekly-${row.id}`}
              >
                {refreshing ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <RefreshCw size={12} className="mr-1.5" />}
                Refresh from timesheets
              </Button>
            )}
          </div>

          {/* Deliverables checklist */}
          <div>
            <Label className="label-overline">Deliverables this week</Label>
            {canEditContent && (
              <div className="flex items-center gap-2 mt-1">
                <Input
                  className="rounded-sm"
                  placeholder="e.g. Logo set v2 · Landing page hero"
                  value={deliverableInput}
                  onChange={(e) => setDeliverableInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDeliverable(); } }}
                  data-testid={`weekly-deliverable-input-${row.id}`}
                />
                <Button type="button" variant="outline" className="rounded-sm shrink-0" onClick={addDeliverable} data-testid={`weekly-deliverable-add-${row.id}`}>
                  <Plus size={14} className="mr-1.5" />Add
                </Button>
              </div>
            )}
            {form.deliverables.length > 0 && (
              <ul className="mt-2 space-y-1.5" data-testid={`weekly-deliverable-list-${row.id}`}>
                {form.deliverables.map((d, i) => (
                  <li key={d._uid} className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded-sm px-2 py-1.5">
                    <button type="button" onClick={() => canEditContent && toggleDeliverable(d._uid)} className="shrink-0" data-testid={`weekly-deliverable-toggle-${row.id}-${i}`}>
                      {d.done ? <CheckCircle2 size={16} className="text-green-600" /> : <Circle size={16} className="text-slate-300" />}
                    </button>
                    <span className={`flex-1 ${d.done ? "line-through text-slate-400" : "text-slate-700"}`}>{d.label}</span>
                    {canEditContent && (
                      <button type="button" onClick={() => removeDeliverable(d._uid)} className="text-slate-400 hover:text-red-600 shrink-0" data-testid={`weekly-deliverable-remove-${row.id}-${i}`}>
                        <X size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Blockers */}
          <div>
            <Label className="label-overline">Blockers</Label>
            <Textarea rows={2} className="rounded-sm mt-1" value={form.blockers} onChange={(e) => setForm((f) => ({ ...f, blockers: e.target.value }))} disabled={!canEditContent} data-testid={`weekly-blockers-${row.id}`} />
          </div>

          {/* Founder's note */}
          {(isMgr || (form.founders_note || "").trim()) && (
            <div>
              <Label className="label-overline">Founder&apos;s note</Label>
              <Textarea
                rows={3} className="rounded-sm mt-1"
                placeholder="A personal note from the founder to the client — appears on the branded PDF and email."
                value={form.founders_note}
                onChange={(e) => setForm((f) => ({ ...f, founders_note: e.target.value }))}
                disabled={!isMgr}
                data-testid={`weekly-founders-note-${row.id}`}
              />
            </div>
          )}

          {/* Actions — Phase 141: two-phase layout. Left = internal drafting
              tools (Save / Submit / Send Test). Right = a distinct,
              highlighted "Share" section for the two client-facing actions,
              with Email to Client locked until a Founder has approved. */}
          <div className="flex items-center justify-between gap-3 flex-wrap pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2 flex-wrap" data-testid={`weekly-drafting-actions-${row.id}`}>
              <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mr-1">Drafting</span>
              {canEditContent && (
                <Button onClick={save} disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" data-testid={`save-weekly-${row.id}`}>
                  {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Pencil size={14} className="mr-1.5" />}
                  Save
                </Button>
              )}
              {canSubmit && (
                <Button onClick={submitForApproval} className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white" data-testid={`submit-weekly-${row.id}`}>
                  <ShieldCheck size={14} className="mr-1.5" /> Submit for approval
                </Button>
              )}
              {isMgr && (
                <Button onClick={sendTest} disabled={sendingTest} variant="outline" className="rounded-sm" data-testid={`send-test-weekly-${row.id}`}>
                  {sendingTest ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FlaskConical size={14} className="mr-1.5" />}
                  Send test to me
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap bg-orange-50 border border-orange-200 rounded-sm px-3 py-2" data-testid={`weekly-share-actions-${row.id}`}>
              <span className="text-[9px] uppercase tracking-wider text-[#F54800] font-semibold mr-0.5">Share</span>
              <Button
                onClick={send} disabled={!canSend}
                className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
                title={canSend ? "Email this report to the client" : "Locked until a Founder marks this report Approved"}
                data-testid={`send-weekly-${row.id}`}
              >
                <Send size={14} className="mr-1.5" /> {row.sent_at ? "Re-send" : "Email to client"}
              </Button>
              <a
                href={`${apiBase}/api/weekly-summary/${row.id}/pdf`} target="_blank" rel="noreferrer"
                className="inline-flex items-center text-xs text-[#F54800] hover:underline gap-1 px-2 h-9"
                data-testid={`download-pdf-weekly-${row.id}`}
              >
                <Download size={13} /> Download PDF
              </a>
              {row.sent_at && (
                <span className="text-[11px] text-slate-500 inline-flex items-center gap-1" data-testid={`weekly-sent-meta-${row.id}`}>
                  <Mail size={11} /> Sent {formatDate(row.sent_at)}{row.opened_at ? ` · opened${row.open_count ? ` (${row.open_count})` : ""}` : ""}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
